import { useQuery } from '@apollo/client';
import { faExternalLink, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { OrderwiseRow } from './OrderwiseRow.tsx';
import * as queries from './queries.ts';
import { withIcon } from '../../../core/components/Icon.tsx';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page/index.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import Table from '../../../core/components/Table.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import { url } from '../../../core/utils/link.ts';
import { CUSTOMER_INTEGRATION_ERP_PATH, CUSTOMER_INTEGRATION_ORDERWISE_ADD_PATH } from '../../constants.ts';

const AddIcon = withIcon(faPlus);
const ExternalLinkIcon = withIcon(faExternalLink);

const OrderwiseIntegrationPage = () => {
    const { t } = useTranslation('integrations');

    const {
        data: data,
        loading: loading,
        refetch: refetch,
    } = useQuery(queries.GET_ORDERWISE_INTEGRATIONS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
    });
    const integrations = data?.orderwiseIntegrations || [];

    return (
        <Layout>
            <SettingsNavigation />
            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <IntegrationBreadcrumb
                        backTooltip={`${t('backTooltipText')} ${t('erp.pageTitle')}`}
                        items={[
                            { label: t('erp.pageTitle'), to: url(CUSTOMER_INTEGRATION_ERP_PATH) },
                            { label: t('orderwise.title') },
                        ]}
                    />

                    <div className="flex gap-2">
                        <Page.HeaderButton asChild>
                            <a href={t('orderwise.documentationLink')} target={'_blank'}>
                                <span className="flex gap-2 items-center">
                                    {t('orderwise.documentation')}
                                    <ExternalLinkIcon />{' '}
                                </span>
                            </a>
                        </Page.HeaderButton>

                        <Page.HeaderButton asChild>
                            <Link to={url(CUSTOMER_INTEGRATION_ORDERWISE_ADD_PATH)}>
                                <AddIcon />
                                {t('orderwise.addButton')}
                            </Link>
                        </Page.HeaderButton>
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6 relative">
                    <Table scrollable className={classnames('w-full table-auto', loading && 'opacity-50')}>
                        <Table.Head>
                            <Table.Row>
                                <Table.HeadCell className="min-w-80 w-80">{t('orderwise.table.name')}</Table.HeadCell>
                                <Table.HeadCell>{t('orderwise.table.orderChannels')}</Table.HeadCell>
                                <Table.HeadCell>{t('orderwise.table.rfqChannels')}</Table.HeadCell>
                                <Table.HeadCell>{t('orderwise.table.masterDataUpdatedAt')}</Table.HeadCell>
                                <Table.HeadCell>{t('orderwise.table.createdAt')}</Table.HeadCell>
                                <Table.HeadCell sticky="right" className="min-w-14 w-14" />
                            </Table.Row>
                        </Table.Head>
                        <Table.Body>
                            {integrations && integrations.length > 0 ? (
                                integrations.map((integration) => (
                                    <OrderwiseRow key={integration.id} integration={integration} refetch={refetch} />
                                ))
                            ) : (
                                <Table.Row>
                                    <Table.Cell colSpan={6} className="text-center text-content-medium">
                                        {t('orderwise.noIntegrations')}
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default OrderwiseIntegrationPage;
